<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { AllowableAssortmentService } from '@/services/allowable-assortment.service';

import { type LaunchCalendarTimelineItemModel } from '@/models/launch-calendar-timeline-item.model';
import { type LaunchCalendarModel } from '@/models/launch-calendar.model';
import { OpenAndUpcomingProductModel } from '@/models/open-and-upcoming-product.model';
import {
  OpenAndUpcomingProductItemDto,
  OpenAndUpcomingSeasonAssortmentDto,
  ProductMainGroups,
} from '@onrunning/backstage-models';

import { mappersMixins } from '@/mixins/mappers.mixins';
import { useUserStore } from '@/stores/user';

import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import LaunchCalendarTimelineItem from '@/components/launch-calendar/launch-calendar-timeline-item.vue';

import { useBPLogger } from '@/composables/business-process-logger';
import { compareDates } from '@/composables/utils';
import { usePricatDownload } from '@/features/pricat-download/usePricatDownload';
import { isDateAfterOrEqual, isDateBeforeOrEqual, today } from '@/utils/temporalUtils';
import { useI18n } from 'vue-i18n';

const $router = useRouter();
const { t } = useI18n();
const { pageViewBPLog } = useBPLogger();
const { selectedAccountId } = storeToRefs(useUserStore());
const { downloadingPricat, getPricatIcon, fetchPricat } = usePricatDownload();

const allowableAssortmentService = new AllowableAssortmentService();

const loading = ref(true);
const selectedSeason = ref('');
const seasons = ref(new Array<string>());
const selectedMainGroup = ref(ProductMainGroups.shoes);
const groupedProducts = ref({} as LaunchCalendarModel);
const productMainGroups = Object.values(ProductMainGroups);

const seasonTitle = computed(() => {
  return t(`viewLaunchCalendar.seasons.${selectedSeason.value.slice(0, 2).toLowerCase()}`, {
    year: `${selectedSeason.value.slice(2)}`,
  });
});

watch(
  () => selectedAccountId.value,
  async (newVal) => {
    if (newVal) {
      loading.value = true;
      await fetchOpenAndUpcomingSeasonAssortments();
    }
  },
);

onMounted(async () => {
  try {
    await fetchOpenAndUpcomingSeasonAssortments();
    console.log(pageViewBPLog(true, { page_name: 'launch-calendar' }));
  } catch (e: any) {
    console.log(pageViewBPLog(false, { page_name: 'launch-calendar', error_message: e }));
  }
});

const launchDateProducts = computed((): LaunchCalendarTimelineItemModel => {
  return groupedProducts.value?.[selectedSeason.value]?.[selectedMainGroup.value] || {};
});

const sortedLaunchDates = computed((): string[] => {
  return Object.keys(launchDateProducts.value).sort(compareDates);
});

const hasSelectedSeasonProducts = computed((): boolean => {
  return (
    groupedProducts.value?.[selectedSeason.value] &&
    Object.keys(groupedProducts.value[selectedSeason.value])?.length !== 0
  );
});

const hasSelectedMainGroupProducts = computed((): boolean => {
  return Object.keys(launchDateProducts.value).length !== 0;
});

const showPricatDownloadButton = computed(() => {
  return seasons.value.length > 0;
});

async function fetchOpenAndUpcomingSeasonAssortments() {
  const productsPerSeasons = await allowableAssortmentService.getOpenAndUpcomingSeasonAssortment(
    selectedAccountId.value,
  );

  loading.value = false;
  seasons.value = [];
  groupedProducts.value = {};

  productsPerSeasons.forEach((productsPerSeason) => {
    seasons.value.push(productsPerSeason.season.name);
    groupedProducts.value[productsPerSeason.season.name] = groupProducts(productsPerSeason.products);
  });

  selectedSeason.value = preselectSeason(productsPerSeasons);
}

function initializeMainGroupGrouping() {
  const productMainGroupMap: Record<string, LaunchCalendarTimelineItemModel> = {};

  Object.values(ProductMainGroups).forEach((mainGroup) => {
    productMainGroupMap[mainGroup] = {};
  });

  return productMainGroupMap;
}

function groupProducts(products: OpenAndUpcomingProductItemDto[]) {
  const productMainGroupMap = initializeMainGroupGrouping();

  products.forEach((product) => {
    if (!productMainGroupMap[product.productTypeName][product.productPhaseInDate]) {
      productMainGroupMap[product.productTypeName][product.productPhaseInDate] = {};
    }

    if (!productMainGroupMap[product.productTypeName][product.productPhaseInDate][product.productModelName]) {
      productMainGroupMap[product.productTypeName][product.productPhaseInDate][product.productModelName] = [];
    }

    const matchedProducts =
      productMainGroupMap[product.productTypeName][product.productPhaseInDate][product.productModelName];

    productMainGroupMap[product.productTypeName][product.productPhaseInDate][product.productModelName] = [
      ...matchedProducts,
      mapLaunchCalendarProductModel(product),
    ];
  });

  return productMainGroupMap;
}

function preselectSeason(openAndUpcomingSeasons: OpenAndUpcomingSeasonAssortmentDto[]) {
  const currentDate = today();

  let activeSellInPeriodSeasonIndex = openAndUpcomingSeasons.findIndex((productSeasonGroup) => {
    // Shoes are the primary assortments and hence we check the shoe sell-in period
    const shoeSellInPeriod = productSeasonGroup.season.seasonSellInPeriods.find(
      (sellInPeriod) => sellInPeriod.productType.name.toLowerCase() === ProductMainGroups.shoes,
    );

    if (shoeSellInPeriod) {
      return (
        isDateBeforeOrEqual(shoeSellInPeriod.startDate, currentDate) &&
        isDateAfterOrEqual(shoeSellInPeriod.endDate, currentDate)
      );
    }
  });

  // Choose the first season from the response if an active shoe sell-in period is not found in a season
  if (activeSellInPeriodSeasonIndex === -1) {
    activeSellInPeriodSeasonIndex = 0;
  }

  return openAndUpcomingSeasons[activeSellInPeriodSeasonIndex]?.season?.name || '';
}

function mapLaunchCalendarProductModel(product: OpenAndUpcomingProductItemDto) {
  const productModel = new OpenAndUpcomingProductModel();

  productModel.productExternalNumber = product.productExternalNumber;
  productModel.productGender = mappersMixins.genderValueToShortString(product.productGender);
  productModel.productImageUrl = product.productImageUrl;
  productModel.productModelName = product.productModelName;
  productModel.productName = product.productName;
  productModel.productPhaseInDate = product.productPhaseInDate;
  productModel.productTypeName = product.productTypeName;
  productModel.productInitialPhaseInDate = product.productInitialPhaseInDate;

  return productModel;
}

function selectMainGroup(mainGroup: ProductMainGroups) {
  selectedMainGroup.value = mainGroup;
}

function selectSeason(season: string) {
  selectedSeason.value = season;
}

function handleBackButton() {
  $router.go(-1);
}
</script>

<template>
  <div class="view-launch-calendar-cnt">
    <div class="title-toolbar">
      <div class="title-toolbar-header">
        <BackstageIcon
          name="arrow-left"
          class="back-icon"
          @click="handleBackButton"
          data-cy="launch-overview-back-button">
        </BackstageIcon>
        <h3 class="title">{{ $t('viewLaunchCalendar.launch_calendar_title') }}</h3>
      </div>

      <div v-if="showPricatDownloadButton" class="pricat-download" data-cy="pricat-download">
        <BackstageButton
          name="arrow-down"
          class="pricat-button"
          :icon-right="getPricatIcon"
          :disabled="downloadingPricat"
          data-cy="launch-overview-pricat-button-cnt"
          data-cy-button="launch-overview-pricat-button">
          {{ $t('viewLaunchCalendar.pricat.download_pricat_button') }}
        </BackstageButton>
        <div class="pricat-dropdown-container" data-cy="pricat-dropdown-container">
          <ul class="pricat-list">
            <li
              v-for="season in seasons"
              :key="season"
              class="pricat-list-item"
              :class="{ disabled: downloadingPricat }"
              @click="fetchPricat(season)">
              {{ $t('viewLaunchCalendar.pricat.download_pricat_button') }} {{ season }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loader"></div>

    <div v-else>
      <div class="launch-calendar-actions">
        <div class="nav-pills">
          <nav>
            <a
              v-for="productMainGroup in productMainGroups"
              :key="productMainGroup"
              class="link"
              :class="{ selected: productMainGroup === selectedMainGroup }"
              :data-cy="`launch-calendar-${productMainGroup}`"
              @click="selectMainGroup(productMainGroup)">
              <span>{{ $t(`viewLaunchCalendar.${productMainGroup}`) }}</span>
            </a>
          </nav>
        </div>
        <div class="nav-pills">
          <nav>
            <a
              v-for="season in seasons"
              :key="season"
              class="link"
              :class="{ selected: season === selectedSeason }"
              :data-cy="`launch-calendar-${season}`"
              @click="selectSeason(season)">
              <span>{{ season.toUpperCase() }}</span>
            </a>
          </nav>
        </div>
      </div>

      <div v-if="hasSelectedSeasonProducts" class="season-title">
        <h3 class="title">
          {{ seasonTitle }}
        </h3>
        <h3 class="title sub-title">
          {{ `(${$t('viewLaunchCalendar.season_subtitle')})` }}
        </h3>
      </div>
      <div class="timeline-cnt">
        <div v-if="!hasSelectedMainGroupProducts" class="no-items">
          <p>{{ $t(`viewLaunchCalendar.no_products`) }}</p>
        </div>
        <div v-else class="timeline">
          <launch-calendar-timeline-item
            v-for="launchDate in sortedLaunchDates"
            :key="launchDate"
            :launch-date="launchDate"
            :product-models="groupedProducts[selectedSeason][selectedMainGroup][launchDate]" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.view-launch-calendar-cnt {
  @include common-container;
  @include container-background;
  position: relative;

  // Always show the scroll bar wherever there's a horizontal scroll
  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 7px;
    height: 6px;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: #7d7d7d;
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }

  .title {
    font-size: 1.25rem;
    font-weight: 400;
    padding-left: 10px;
  }

  .launch-calendar-actions {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;

    nav {
      a {
        padding: 0px 50px;
      }
    }
  }

  .season-title h3 {
    text-align: center;
    margin-bottom: 10px;
    padding-top: 20px;
  }

  .sub-title {
    color: var(--field-text-disabled-color);
    margin-top: 0px;
  }

  .timeline-cnt {
    margin: auto;
    padding: 10px;

    .timeline {
      display: flex;
      overflow-x: scroll;
      width: auto;
      flex-direction: row;
      padding-bottom: 30px;
    }

    .no-items {
      min-height: 150px;
      margin: auto;
    }
  }

  .title-toolbar {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    &-header {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .back-icon {
      cursor: pointer;
    }
  }

  .pricat-download {
    max-width: 90%;
    position: relative;

    .pricat-button {
      border: none;
      outline: none;
    }

    .pricat-dropdown-container {
      display: none;
      position: absolute;
      right: 0;
      top: 0;
      margin-top: 42px;
      width: 13.625rem;
      border-radius: 0.6rem;
      box-shadow: 0px 5px 5px 0px rgba(0, 0, 0, 0.1);

      .pricat-list {
        padding: 0px;
        margin: 0;
        border-radius: 0.6rem;
        overflow: hidden;

        .pricat-list-item {
          width: 100%;
          border: none;
          cursor: pointer;
          font-size: small;
          text-align: left;
          padding: 0.825rem;
          border-bottom: 1px solid;
          background-color: var(--primary-bg-color);
          border-bottom-color: var(--bg-remark-selected-color);
          &.active,
          &:hover {
            background-color: var(--bg-remark-selected-color);
          }
          &.disabled {
            pointer-events: none;
            cursor: not-allowed;
          }
        }
      }
    }
  }

  .pricat-download:hover .pricat-dropdown-container,
  .pricat-download:active .pricat-dropdown-container {
    display: block;
  }
}
</style>
